import { API_BASE_URL } from "@/lib/api/client";

export type HandoffDecision = "rebind" | "clear";

const HANDOFF_TIMEOUT_MS = 10_000;

type AccountRead = { kind: "email"; email: string } | { kind: "reject" } | { kind: "unavailable" };

async function readAccountEmail(bearer: string, signal: AbortSignal): Promise<AccountRead> {
  if (signal.aborted) {
    throw new DOMException("The account read was aborted.", "AbortError");
  }
  try {
    const response = await fetch(`${API_BASE_URL}/v1/account`, {
      headers: {
        Accept: "application/json",
        Authorization: `Bearer ${bearer}`,
      },
      cache: "no-store",
      signal,
    });
    if (response.status === 408 || response.status === 429 || response.status >= 500) {
      return { kind: "unavailable" };
    }
    if (!response.ok) return { kind: "reject" };
    const body = (await response.json().catch((error: unknown) => {
      if (signal.aborted) throw error;
      return null;
    })) as {
      account?: { email?: unknown };
    } | null;
    const email = body?.account?.email;
    if (typeof email !== "string" || email.length === 0) return { kind: "reject" };
    return { kind: "email", email };
  } catch (error) {
    if (signal.aborted) throw error;
    return { kind: "unavailable" };
  }
}

async function decideOnce(
  previousKey: string,
  nextKey: string,
  cancel: AbortSignal,
): Promise<HandoffDecision | "retry"> {
  const timeout = AbortSignal.timeout(HANDOFF_TIMEOUT_MS);
  const attempt = AbortSignal.any([cancel, timeout]);
  try {
    const [previous, next] = await Promise.all([
      readAccountEmail(previousKey, attempt),
      readAccountEmail(nextKey, attempt),
    ]);
    if (cancel.aborted) {
      throw new DOMException("The handoff was cancelled.", "AbortError");
    }
    if (previous.kind === "unavailable" || next.kind === "unavailable") return "retry";
    if (previous.kind === "email" && next.kind === "email" && previous.email === next.email) {
      return "rebind";
    }
    return "clear";
  } catch (error) {
    if (cancel.aborted) throw error;
    if (timeout.aborted) return "retry";
    throw error;
  }
}

export async function decideRecoveryCodesHandoff(
  previousKey: string,
  nextKey: string,
  cancel: AbortSignal,
): Promise<HandoffDecision> {
  const first = await decideOnce(previousKey, nextKey, cancel);
  if (first !== "retry") return first;
  const second = await decideOnce(previousKey, nextKey, cancel);
  return second === "retry" ? "clear" : second;
}
